import { Box } from '@mui/material';
import { ConfigProvider } from 'antd';
import { debounce } from 'lodash';
import React, { useEffect } from 'react';
import { renderRoutes } from 'react-router-config';
import { Router } from 'react-router-dom';

import { type UserInfo } from '@/api';
import { ObserverStore } from '@/component';
// import { AiSeeCustomerFab } from '@/component/aisee-customer-widget';
import routes from '@/router';
import { guardRoot as GuardRoot } from '@/router/guard';
import { getUserInfoFromStorage, onTokenExpired } from '@/util/authority';
import { USER_IMG_EXTRANET } from '@/util/dev-tool';
import history from '@/util/history';
import Logger from '@/util/logger';
import LS from '@/util/ls';
import { LocalCacheEnum } from '@/util/ls/useLocalstorage';
import { isMobileDevices, isMobilePages } from '@/util/mobile';

import { getDefaultSkeleton } from './component/m-common/m-spin/skeleton';
import MobileLoading from './component/mobile/loading';

const logger = Logger.create('[App]');

/** 伽利略上报信息 */
type MSGType = {
  msg: string;
  customFields: Record<string, any>;
};

declare global {
  interface Window {
    __AISEE_INITAL_STATES__: {
      nickname?: string;
    };
    /** 伽利略sdk上报 */
    AegisV2?: {
      setConfig: (val: Record<string, any>) => void;
      info: (val: MSGType) => void;
      error: (val: MSGType) => void;
      report: (val: MSGType) => void;
    };
    /** 小满相关定义 */
    xmMeter?: any;
  }
}

const Setup = ObserverStore(({ store }) => {
  // The login redirect Uri
  const { hash, href } = location;
  store.setHashInfo({ hash, href });

  // 检测并保存 URL 中的 OAuth code 参数
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    if (code) {
      // 保存 code 和 state 到 localStorage
      LS.set(LocalCacheEnum.AISEE_GIT_OAUTH_CODE, {
        code,
        state,
        timestamp: Date.now(),
      });
    }
  }, []);

  useEffect(() => {
    let userInfo: UserInfo | null = null;
    // Analyze user infromation from store and storage
    if (store?.userInfo) {
      logger.info({ storeUserInfo: store.userInfo });
      userInfo = store.userInfo;
    } else {
      const userInfoData = getUserInfoFromStorage();
      if (userInfoData) {
        try {
          userInfo = JSON.parse(userInfoData);
          logger.info({ storageUserInfo: userInfo });
        } catch (error) {
          logger.error(`Parse user info from storage error: ${error}`);
        }
      }
    }
    // Can not analyze user information
    if (!userInfo) {
      logger.info('Can not found user info from store and storage');
      onTokenExpired();
    } else {
      if (userInfo?.user_info?.avatar) {
        userInfo.user_info.avatar = userInfo.user_info.avatar.replace(
          USER_IMG_EXTRANET,
          ''
        );
      }
      store.setUserInfo(userInfo);
      store.setUser(userInfo?.user_info?.nickname);
    }
    window.__AISEE_INITAL_STATES__.nickname = userInfo?.user_info?.nickname;
    /** 关联伽利略sdk uid */
    window?.AegisV2?.setConfig({
      uid: userInfo?.user_info?.nickname,
    });
  }, []);

  useEffect(() => {
    const baseWidth = 376;
    const baseFontSize = 16;

    const applyFontSize = () => {
      if (isMobilePages(location.pathname) && store.useMobileUI) {
        const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
        const newFontSize = (viewportWidth / baseWidth) * baseFontSize;
        document.documentElement.style.fontSize = `${newFontSize}px`;
      } else {
        document.documentElement.style.removeProperty('font-size');
      }
    };

    const syncViewport = debounce(() => {
      const next = isMobileDevices();
      if (next !== store.isMobileDevice) {
        store.setIsMobileDevice(next);
        store.setIsMobile(next);
      }
      applyFontSize();
    }, 50);

    applyFontSize();

    window.addEventListener('resize', syncViewport);
    window.visualViewport?.addEventListener('resize', syncViewport);
    return () => {
      syncViewport.cancel();
      window.removeEventListener('resize', syncViewport);
      window.visualViewport?.removeEventListener('resize', syncViewport);
    };
  }, [location.pathname, store.useMobileUI]);

  return null;
});

const SuspenseFallback = ObserverStore(({ store }) => {
  return store.useMobileUI ? (
    /** 移动端设备使用特殊的骨架屏幕加载 */
    <MobileLoading />
  ) : (
    <Box sx={{ mt: '40px', ml: '40px', width: '40%' }}>
      {getDefaultSkeleton(4)}
    </Box>
  );
});

function app() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#585CEE',
          colorLink: '#585CEE',
        },
        components: {
          Steps: {
            navArrowColor: '#585CEE',
            colorPrimary: '#585CEE',
          },
        },
      }}
    >
      <Router history={history}>
        <React.Suspense fallback={<SuspenseFallback />}>
          {renderRoutes(routes)}
        </React.Suspense>
        <Setup />
        {/* <AiSeeCustomerFab /> */}
        <GuardRoot path={location.pathname} search={location.search} />
      </Router>
    </ConfigProvider>
  );
}

export default app;
